// brainGraphAdapter — client-side VIEW-MODEL that assembles the neural graph from
// ALREADY-FETCHED endpoint payloads. NO backend change; NO invented data.
//
// It is DECISION-CENTRIC and BOUNDED: it seeds on the top priority decisions and
// walks only REAL join keys 1–2 hops out (never an unbounded full-graph render).
// This is a decision interface, not a KPI dashboard.
//
// REAL joins only (every edge carries its provenance):
//   decision.item_id            === outcome.subject_ref_id        -> OUTCOME
//   outcome.goal_ids[]          (+ decision.goal_alignment.goal_ids[]) -> GOAL
//   outcome.correlation_id      === prediction.correlation_id     -> CORRELATION
//   outcome.evidence[].ref_id                                     -> EVIDENCE
//   journal.links.recommendation_id === decision.item_id          -> MEMORY
//   goal.parent_goal_id         (both goals already in set)       -> HIERARCHY
//   observation.related_*       === decision.related_*            -> (OBSERVATION via EVIDENCE-style link)
//
// Sources: services/executive_ranking.py (queue items), services/outcomes/adapters/
// recommendations.py (outcome shape), services/goals/linkage.py + hierarchy (goals),
// routers/journal.py (entry.links), routers/performance.py (observations).

const DEFAULTS = { maxDecisions: 3, maxPerType: 6 };

function asArray(x) {
  return Array.isArray(x) ? x : [];
}

function journalLinks(entry) {
  if (!entry) return {};
  return entry.links || (entry.entry && entry.entry.links) || {};
}

function journalId(entry) {
  return (entry && (entry.entry_id || (entry.entry && entry.entry.entry_id))) || null;
}

export function buildDecisionGraph(input, options = {}) {
  const opts = { ...DEFAULTS, ...options };
  const decisions = asArray(input.decisions).slice(0, opts.maxDecisions);
  const outcomes = asArray(input.outcomes);
  const goals = asArray(input.goals);
  const predictions = asArray(input.predictions);
  const observations = asArray(input.observations);
  const journalEntries = asArray(input.journalEntries);

  const goalById = new Map(goals.map((g) => [g.goal_id, g]));
  const outcomeByRef = new Map();
  for (const o of outcomes) {
    if (o.subject_ref_id != null) outcomeByRef.set(o.subject_ref_id, o);
  }
  const predsByCorr = new Map();
  for (const p of predictions) {
    if (p.correlation_id) {
      if (!predsByCorr.has(p.correlation_id)) predsByCorr.set(p.correlation_id, []);
      predsByCorr.get(p.correlation_id).push(p);
    }
  }

  const nodes = new Map(); // id -> node
  const edges = new Map(); // id -> edge (weight accumulates)
  let truncated = false;

  const addNode = (id, cluster, label, refId, meta) => {
    if (!nodes.has(id)) nodes.set(id, { id, cluster, label: label || refId || id, refId, size: 0, meta: meta || {} });
    return nodes.get(id);
  };
  const addEdge = (source, target, type, provenance) => {
    if (!nodes.has(source) || !nodes.has(target)) return;
    const id = `${source}->${target}:${type}`;
    const existing = edges.get(id);
    if (existing) existing.weight += 1;
    else edges.set(id, { id, source, target, type, weight: 1, provenance });
  };

  const linkGoal = (fromId, goalId) => {
    if (!goalId) return;
    const g = goalById.get(goalId);
    const gNode = addNode(`goal:${goalId}`, "GOAL", g ? g.title || goalId : goalId, goalId, {
      level: g && g.level,
      present: !!g,
    });
    addEdge(fromId, gNode.id, "GOAL", "outcome.goal_ids[] / goal_alignment.goal_ids[]");
  };

  for (const d of decisions) {
    const itemId = d.item_id;
    if (itemId == null) continue;
    const dId = `decision:${itemId}`;
    addNode(dId, "DECISION", d.title || itemId, itemId, {
      item_type: d.item_type,
      business_unit: d.business_unit,
      basis: d.impact && d.impact.basis,
      dollars_per_day: d.impact && d.impact.dollars_per_day,
      rank: d.rank,
    });

    // decision-level goal alignment (Phase 1 often empty; goal-weighted-preview populates)
    for (const gid of asArray(d.goal_alignment && d.goal_alignment.goal_ids)) linkGoal(dId, gid);

    // decision -> outcome (subject_ref_id join)
    const outcome = outcomeByRef.get(itemId);
    if (outcome) {
      const oId = `outcome:${outcome.outcome_id}`;
      addNode(oId, "OUTCOME", `${outcome.result || outcome.status || "OUTCOME"}`, outcome.outcome_id, {
        result: outcome.result,
        status: outcome.status,
        correlation_id: outcome.correlation_id,
      });
      addEdge(dId, oId, "OUTCOME", "outcome.subject_ref_id === decision.item_id");

      // outcome -> goals
      for (const gid of asArray(outcome.goal_ids)) linkGoal(oId, gid);

      // outcome -> evidence (bounded)
      const ev = asArray(outcome.evidence);
      if (ev.length > opts.maxPerType) truncated = true;
      for (const e of ev.slice(0, opts.maxPerType)) {
        if (!e || !e.ref_id) continue;
        const eId = `evidence:${e.collection || "?"}:${e.ref_id}:${e.field || "?"}`;
        addNode(eId, "EVIDENCE", e.field || e.ref_id, e.ref_id, {
          collection: e.collection,
          as_of: e.as_of,
          value: e.value,
        });
        addEdge(oId, eId, "EVIDENCE", "outcome.evidence[].ref_id");
      }

      // outcome -> predictions via shared correlation_id
      const corr = outcome.correlation_id;
      if (corr && predsByCorr.has(corr)) {
        const preds = predsByCorr.get(corr);
        if (preds.length > opts.maxPerType) truncated = true;
        for (const p of preds.slice(0, opts.maxPerType)) {
          const pId = `prediction:${p.prediction_id}`;
          addNode(pId, "PREDICTION", p.prediction_type || p.prediction_id, p.prediction_id, {
            confidence_tier: p.confidence_tier,
          });
          addEdge(oId, pId, "CORRELATION", "shared correlation_id");
        }
      }
    }

    // decision -> memory (journal.links.recommendation_id)
    for (const entry of journalEntries) {
      const links = journalLinks(entry);
      if (links.recommendation_id && links.recommendation_id === itemId) {
        const jId = journalId(entry);
        const mId = `memory:${jId}`;
        addNode(mId, "MEMORY", entry.decision_type || (entry.entry && entry.entry.decision_type) || "journal", jId, {
          disposition: entry.disposition || (entry.entry && entry.entry.disposition),
        });
        addEdge(dId, mId, "MEMORY", "journal.links.recommendation_id === decision.item_id");
        for (const gid of asArray(links.goal_ids)) linkGoal(mId, gid);
      }
    }

    // decision -> observations that share a real related_* ref (bounded)
    const relIds = [d.related_campaign_id, d.related_offer_id].filter(Boolean);
    if (relIds.length) {
      let added = 0;
      for (const obs of observations) {
        const obsRefs = [obs.related_campaign_id, obs.related_offer_id, obs.campaign_id, obs.offer_id].filter(Boolean);
        if (obsRefs.some((r) => relIds.includes(r))) {
          if (added >= opts.maxPerType) {
            truncated = true;
            break;
          }
          const obsId = `observation:${obs.observation_id || obs.id}`;
          addNode(obsId, "OBSERVATION", obs.title || obs.summary || obsId, obs.observation_id || obs.id, {});
          addEdge(dId, obsId, "EVIDENCE", "observation.related_* === decision.related_*");
          added += 1;
        }
      }
    }
  }

  // goal hierarchy edges — only between goals ALREADY in the graph (bounded, real)
  for (const node of nodes.values()) {
    if (node.cluster !== "GOAL") continue;
    const g = goalById.get(node.refId);
    if (g && g.parent_goal_id && nodes.has(`goal:${g.parent_goal_id}`)) {
      addEdge(`goal:${g.parent_goal_id}`, node.id, "HIERARCHY", "goal.parent_goal_id (ancestors)");
    }
  }

  // node.size = REAL degree (count of incident edges). deterministic.
  for (const e of edges.values()) {
    nodes.get(e.source).size += 1;
    nodes.get(e.target).size += 1;
  }

  const nodeList = Array.from(nodes.values());
  const edgeList = Array.from(edges.values());
  return {
    nodes: nodeList,
    edges: edgeList,
    truncated,
    clusterCounts: clusterCounts(nodeList),
    maxSize: nodeList.reduce((m, n) => Math.max(m, n.size), 0),
    maxWeight: edgeList.reduce((m, e) => Math.max(m, e.weight), 0),
  };
}

export function clusterCounts(nodes) {
  const out = {};
  for (const n of asArray(nodes)) out[n.cluster] = (out[n.cluster] || 0) + 1;
  return out;
}

// adjacency for keyboard traversal of the graph (pure).
export function buildAdjacency(edges) {
  const adj = new Map();
  for (const e of asArray(edges)) {
    if (!adj.has(e.source)) adj.set(e.source, []);
    if (!adj.has(e.target)) adj.set(e.target, []);
    adj.get(e.source).push(e.target);
    adj.get(e.target).push(e.source);
  }
  return adj;
}
